import type { FormField, FormSettings } from './form-schema'

export const STAFF_HIRING_APPLICATION_FORM_SLUG = 'event-staff-application-2026'
export const STAFF_HIRING_APPLICATION_FORM_NAME =
  'HTY Event Staff Application 2026'

export const STAFF_HIRING_POSITION_TITLE =
  'Photo Booth + Event Activation Attendant'

export type StaffHiringTerminalOutcome = {
  submitText: string
  heading: string
  body: string
}

export function resolveStaffHiringTerminalOutcome(
  answers: Record<string, unknown>,
): StaffHiringTerminalOutcome | null {
  if (answers.workAuthorized === 'No') {
    return {
      submitText: 'Finish',
      heading: 'Thanks for your interest.',
      body: 'This role requires current legal eligibility to work in the U.S., so we’re not able to move forward with this application.',
    }
  }

  if (answers.ageEligible === 'No') {
    return {
      submitText: 'Finish',
      heading: 'Thanks for your interest.',
      body: 'This role requires applicants to be 18 or older, so we’re not able to move forward with this application.',
    }
  }

  return null
}

const availabilityDays = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

const eventBlocks = [
  'Weekday evenings',
  'Friday evenings',
  'Saturdays',
  'Sundays',
  'Weekday daytime',
  'Late nights',
]

export const STAFF_HIRING_APPLICATION_FORM_FIELDS: Array<FormField> = [
  {
    fieldId: 'workAuthorized',
    type: 'radio',
    label: 'Legally eligible to work in the U.S.?',
    required: true,
    options: ['Yes', 'No'],
    sortOrder: 0,
  },
  {
    fieldId: 'ageEligible',
    type: 'radio',
    label: 'Are you 18 or older?',
    required: true,
    options: ['Yes', 'No'],
    showWhen: [{ fieldId: 'workAuthorized', operator: 'equals', value: 'Yes' }],
    sortOrder: 1,
  },
  {
    fieldId: 'liftCarry',
    type: 'radio',
    label:
      'This work can involve standing for several hours and lifting/carrying booth gear. Can you do that safely, with or without reasonable accommodation?',
    required: true,
    options: ['Yes', 'Yes, with accommodations or limits', 'No'],
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 2,
  },
  {
    fieldId: 'generalEventAvailability',
    type: 'radio',
    label:
      'Most HTY events are evenings and weekends. Is that generally realistic for your schedule?',
    required: true,
    options: ['Yes', 'Sometimes', 'Not sure yet', 'Probably not'],
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 3,
  },
  {
    fieldId: 'fullName',
    type: 'text',
    label: 'Full name',
    required: true,
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 4,
  },
  {
    fieldId: 'email',
    type: 'email',
    label: 'Email',
    required: true,
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 5,
  },
  {
    fieldId: 'phone',
    type: 'phone',
    label: 'Phone',
    required: true,
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 6,
  },
  {
    fieldId: 'city',
    type: 'text',
    label: 'What city/neighborhood do you live in?',
    required: true,
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 7,
  },
  {
    fieldId: 'availabilityDays',
    type: 'multiselect',
    label: 'Which days can you usually work events?',
    helpText: 'Check all that apply. You can share specific conflicts next.',
    required: true,
    options: availabilityDays,
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 8,
  },
  {
    fieldId: 'eventBlocks',
    type: 'multiselect',
    label: 'What event times are usually realistic for you?',
    helpText: 'Check all that apply.',
    required: true,
    options: eventBlocks,
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 9,
  },
  {
    fieldId: 'knownUnavailableDates',
    type: 'text',
    label: 'Any upcoming dates you already know you cannot work?',
    helpText: 'Optional. A short list is enough.',
    required: false,
    validation: { maxLength: 500 },
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 10,
  },
  {
    fieldId: 'transportSelf',
    type: 'radio',
    label: 'Reliable transportation to Seattle-area venues?',
    helpText: 'Most venues are in the Seattle metro area.',
    required: true,
    options: ['Yes', 'No', 'Depends on location/transit'],
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 11,
  },
  {
    fieldId: 'canTransportGear',
    type: 'radio',
    label:
      'Advanced roles may involve picking up gear from SODO. Could you do this?',
    helpText: 'Answering “No” is completely fine for standard event staff.',
    required: true,
    options: ['Yes', 'Maybe, depends on gear/event', 'No'],
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 12,
  },
  {
    fieldId: 'guestFacingExperience',
    type: 'radio',
    label: 'Have you done guest-facing work before?',
    helpText:
      'Events, hospitality, customer service, theater, brand ambassador, photo/video, or similar all count.',
    required: true,
    options: ['Yes', 'Not formally, but I’m comfortable with people', 'No'],
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 13,
  },
  {
    fieldId: 'guestComfortAnswer',
    type: 'textarea',
    label:
      'Briefly, why would you be good at making guests feel comfortable and welcome?',
    helpText: '1-3 sentences is plenty.',
    required: true,
    validation: { maxLength: 1200 },
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 14,
  },
  {
    fieldId: 'photoVideoExperience',
    type: 'textarea',
    label: 'Any photo/video/tech experience we should know about?',
    helpText: 'None needed. This is optional.',
    required: false,
    validation: { maxLength: 1200 },
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 15,
  },
  {
    fieldId: 'applicantLink',
    type: 'text',
    label: 'Resume, LinkedIn, Instagram, or portfolio link - optional',
    required: false,
    validation: { maxLength: 500 },
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 16,
  },
  {
    fieldId: 'source',
    type: 'select',
    label: 'How did you hear about this role?',
    required: true,
    options: [
      'HTY staff/client referral',
      'Friend or colleague',
      'Instagram',
      'Facebook or community group',
      'Job post or job board',
      'Event or hospitality group',
      'Other',
    ],
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 17,
  },
  {
    fieldId: 'sourceOther',
    type: 'text',
    label: 'If other, where did you hear about it?',
    required: false,
    showWhen: [{ fieldId: 'source', operator: 'equals', value: 'Other' }],
    sortOrder: 18,
  },
  {
    fieldId: 'videoIntroConsent',
    type: 'radio',
    label: 'Would you like to upload a short video intro?',
    helpText:
      'Optional. A casual 60-90 second phone video can help us understand your guest-facing style. No editing or perfect lighting needed. We look for warmth and communication, not appearance or polish. Videos are used only for hiring review by Hello There You, stored privately with admin-only access, not shared publicly or with event staff, and deleted after the hiring process. Choose No or skip this question if video is not comfortable or accessible for you.',
    required: false,
    options: ['Yes', 'No'],
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 19,
  },
  {
    fieldId: 'videoIntroUpload',
    type: 'file',
    label: 'Upload your video intro',
    helpText:
      'Keep it casual and around 60-90 seconds. MP4, MOV, or WebM up to 250 MB.',
    required: false,
    showWhen: [
      { fieldId: 'videoIntroConsent', operator: 'equals', value: 'Yes' },
    ],
    sortOrder: 20,
  },
  {
    fieldId: 'videoIntroSkipReason',
    type: 'textarea',
    label: 'No problem - is there anything you’d like us to know?',
    helpText: 'Optional. Skipping the video does not affect your eligibility.',
    required: false,
    validation: { maxLength: 1200 },
    showWhen: [
      { fieldId: 'videoIntroConsent', operator: 'equals', value: 'No' },
    ],
    sortOrder: 21,
  },
  {
    fieldId: 'anythingElse',
    type: 'textarea',
    label: 'Anything else we should know?',
    helpText:
      'Availability notes, transportation context, or anything that makes you a good fit.',
    required: false,
    validation: { maxLength: 1200 },
    showWhen: [{ fieldId: 'ageEligible', operator: 'equals', value: 'Yes' }],
    sortOrder: 22,
  },
]

export const STAFF_HIRING_APPLICATION_FORM_SETTINGS: FormSettings = {
  submitButtonText: 'Submit application',
  successMessage:
    "Thanks for applying. If it looks like a fit, we'll reach out for a quick phone/video conversation.",
  successContent: {
    heading: 'Thanks for applying.',
    body: "If it looks like a fit, we'll reach out for a quick phone/video conversation. We review applications on a rolling basis and move quickly when someone seems like a strong match.",
  },
  introContent: {
    heading: STAFF_HIRING_POSITION_TITLE,
    body: 'Hello There You is hiring part-time event staff for photo and video activations around Seattle.\n\nThis is occasional, on-call event-shift work - mostly evenings and weekends. Most shifts include a few hours on-site plus setup and teardown. Monthly hours depend on event demand and your availability, typically around 6-24 hours per month.\n\nGood fit if you are:\n- Warm and confident with guests\n- Reliable under pressure\n- Comfortable with basic tech\n- Good at following checklists\n- Able to stay calm when events get busy\n- Available for evening and weekend event shifts\n- At least 18\n- Able to get reliably to Seattle-area venues\n- Able to stand for several hours and safely lift/carry event gear, with or without reasonable accommodation\n\nYou do not need to be a professional photographer. We can train the gear. We cannot train showing up on time and making guests feel comfortable.\n\nTraining and supervised events pay $30/hour. Solo-ready event operators earn $35/hour. Washington State Paid Sick Leave applies, with occasional tips, meals, holiday bonuses, and paid travel opportunities depending on the event.',
    buttonText: 'Start application - about 5 minutes',
  },
  allowMultiple: false,
}
